import React, { useEffect, useState } from "react";
import {
  FaPlus,
  FaSpinner,
  FaTrash,
  FaEdit,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaClipboardList,
  FaSync,
} from "react-icons/fa";

const API = process.env.REACT_APP_API_BASE_URL;

interface ClassTestOption {
  id: number;
  title: string;
  level: string;
  chapterRangeDisplay: string;
  totalDurationMinutes: number;
}

interface Batch {
  id: number;
  name: string;
  topic: string;
}

interface Assignment {
  id: number;
  classTestId: number;
  classTestTitle: string;
  level: string;
  chapterRange: string;
  batchId: number;
  batchName: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  totalDurationMinutes: number;
  effectiveStatus: "SCHEDULED" | "ACTIVE" | "COMPLETED";
  totalStudents: number;
  submitted: number;
  graded: number;
  published: number;
  passed: number;
}

interface Props {
  onGrade: (assignmentId: number) => void;
  apiBase?: string;
}

const STATUS_STYLES: Record<string, string> = {
  SCHEDULED: "bg-yellow-100 text-yellow-800",
  ACTIVE: "bg-green-100 text-green-800",
  COMPLETED: "bg-gray-100 text-gray-700",
};

const LEVEL_COLORS: Record<string, string> = {
  A1: "bg-green-100 text-green-800",
  A2: "bg-blue-100 text-blue-800",
  B1: "bg-purple-100 text-purple-800",
  B2: "bg-orange-100 text-orange-800",
};

const fmt = (dt: string) =>
  new Date(dt).toLocaleString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  }) + " (DE)";

const AdminClassTestAssignments: React.FC<Props> = ({ onGrade, apiBase }) => {
  const base = apiBase ?? `${API}/api/admin/class-tests`;
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [tests, setTests] = useState<ClassTestOption[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [form, setForm] = useState({
    classTestId: "",
    batchId: "",
    scheduledStartTime: "",
    scheduledEndTime: "",
    instructions: "",
    allowLateSubmission: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, tRes, bRes] = await Promise.all([
        fetch(`${base}/assignments`, { credentials: "include" }),
        fetch(`${base}`, { credentials: "include" }),
        fetch(`${API}/api/admin/writing-tasks/active-batches`, { credentials: "include" }),
      ]);
      if (aRes.ok) setAssignments(await aRes.json());
      if (tRes.ok) setTests(await tRes.json());
      if (bRes.ok) {
        const d = await bRes.json();
        setBatches(d.data || d);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${base}/assignments`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          classTestId: Number(form.classTestId),
          batchId: Number(form.batchId),
          scheduledStartTime: form.scheduledStartTime || null,
          scheduledEndTime: form.scheduledEndTime || null,
          instructions: form.instructions || null,
          allowLateSubmission: form.allowLateSubmission,
        }),
      });
      if (res.ok) {
        setShowForm(false);
        setForm({ classTestId: "", batchId: "", scheduledStartTime: "", scheduledEndTime: "", instructions: "", allowLateSubmission: false });
        await load();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.message || "Failed to assign class test");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this assignment? Students will lose access.")) return;
    setDeletingId(id);
    try {
      await fetch(`${base}/assignments/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setAssignments((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const selectedTest = tests.find((t) => t.id === Number(form.classTestId));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Assignments</h2>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="p-2 text-gray-500 hover:text-[#009fe3] hover:bg-blue-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <FaSync />
          </button>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] transition-colors"
          >
            <FaPlus /> New Assignment
          </button>
        </div>
      </div>

      {/* Assignment form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Assign Class Test to Batch</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Test</label>
                <select
                  required
                  value={form.classTestId}
                  onChange={(e) => setForm((f) => ({ ...f, classTestId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                >
                  <option value="">Select a class test…</option>
                  {["A1", "A2", "B1", "B2"].map((lvl) => {
                    const lvlTests = tests.filter((t) => t.level === lvl);
                    if (!lvlTests.length) return null;
                    return (
                      <optgroup key={lvl} label={`Level ${lvl}`}>
                        {lvlTests.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.title} — {t.chapterRangeDisplay}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                {selectedTest && (
                  <p className="mt-1 text-xs text-gray-500">
                    Duration: {selectedTest.totalDurationMinutes} min total
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
                <select
                  required
                  value={form.batchId}
                  onChange={(e) => setForm((f) => ({ ...f, batchId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                >
                  <option value="">Select a batch…</option>
                  {["A1", "A2", "B1", "B2"].map((lvl) => {
                    const lvlBatches = batches.filter(
                      (b) =>
                        b.topic?.toUpperCase() === lvl &&
                        (!selectedTest || b.topic?.toUpperCase() === selectedTest.level)
                    );
                    if (!lvlBatches.length) return null;
                    return (
                      <optgroup key={lvl} label={`Level ${lvl}`}>
                        {lvlBatches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </optgroup>
                    );
                  })}
                  {/* Batches whose topic doesn't match any level */}
                  {batches
                    .filter((b) => !["A1","A2","B1","B2"].includes(b.topic?.toUpperCase()))
                    .map((b) => (
                      <option key={b.id} value={b.id}>{b.name} ({b.topic})</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={form.scheduledStartTime}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledStartTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="datetime-local"
                  value={form.scheduledEndTime}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledEndTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions (optional)</label>
              <textarea
                rows={2}
                value={form.instructions}
                onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                placeholder="Any special instructions for students…"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.allowLateSubmission}
                onChange={(e) => setForm((f) => ({ ...f, allowLateSubmission: e.target.checked }))}
                className="rounded border-gray-300 text-[#009fe3]"
              />
              <span className="text-sm text-gray-700">Allow late submission</span>
            </label>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] disabled:opacity-50 transition-colors text-sm font-medium"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaPlus />}
                Assign
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Assignments list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
        </div>
      ) : assignments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
          <FaClipboardList className="mx-auto text-4xl mb-3 text-gray-300" />
          <p className="font-medium">No assignments yet.</p>
          <p className="text-sm mt-1">Create one above to assign a class test to a batch.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Test</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Batch</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Schedule</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Progress</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {assignments.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{a.classTestTitle}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${LEVEL_COLORS[a.level] || "bg-gray-100 text-gray-700"}`}>
                        {a.level}
                      </span>
                      <span className="text-xs text-gray-500">{a.chapterRange?.replace(/_/g, " ")}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-gray-800">
                      <FaUsers className="text-[#009fe3]" size={12} />
                      {a.batchName}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{a.totalStudents} student(s)</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[a.effectiveStatus] || "bg-gray-100 text-gray-700"}`}>
                      {a.effectiveStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-600">
                    {a.scheduledStartTime ? (
                      <>
                        <div>{fmt(a.scheduledStartTime)}</div>
                        {a.scheduledEndTime && <div className="text-gray-400">→ {fmt(a.scheduledEndTime)}</div>}
                      </>
                    ) : (
                      <span className="text-gray-400">Open</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-800">{a.submitted}</span>
                      <span className="text-gray-400">/{a.totalStudents}</span>
                      <span className="text-gray-500 ml-1">submitted</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {a.graded} graded · {a.published} published · {a.passed} passed
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onGrade(a.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#009fe3] text-white text-xs font-medium rounded-lg hover:bg-[#007cb5] transition-colors"
                        title="Grade attempts"
                      >
                        <FaEdit size={11} /> Grade
                        {a.submitted > a.graded && (
                          <span className="bg-white/30 rounded px-1">{a.submitted - a.graded}</span>
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(a.id)}
                        disabled={deletingId === a.id}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {deletingId === a.id ? <FaSpinner className="animate-spin" size={13} /> : <FaTrash size={13} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminClassTestAssignments;
